import React from 'react';
import { Bot, BookOpen, Brain, Shield, CheckCircle2 } from 'lucide-react';
import { TutorConfig } from './AITutorBuilderContent';
import Icon from '@/components/ui/AppIcon';


interface Props {
  config: TutorConfig;
  currentStep: number;
}

export default function TutorBuilderPreview({ config, currentStep }: Props) {
  const completionPct = Math.round((currentStep / 4) * 100);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 sticky top-6">
      <h3 className="text-sm font-bold text-foreground mb-4">Live Preview</h3>

      {/* Tutor card */}
      <div className="flex flex-col items-center text-center p-5 bg-gradient-to-b from-primary/5 to-background rounded-xl border border-primary/20 mb-5">
        <span className="text-5xl mb-3">{config.avatar}</span>
        <p className="text-lg font-extrabold text-foreground mb-0.5">{config.name || 'Your Tutor'}</p>
        <p className="text-xs text-muted-foreground capitalize">{config.tone} · {config.personality}</p>
        <div className="flex items-center gap-1.5 mt-2">
          <span className="w-2 h-2 bg-primary rounded-full pulse-dot" />
          <span className="text-xs text-primary font-medium">Ready to teach</span>
        </div>
      </div>

      {/* Config checklist */}
      <div className="space-y-2 mb-5">
        {[
          { label: 'Identity configured', done: !!config.name, icon: Bot },
          { label: `${config.courses.length} course${config.courses.length !== 1 ? 's' : ''} selected`, done: config.courses.length > 0, icon: BookOpen },
          { label: `${config.explanationStyle} style set`, done: !!config.explanationStyle, icon: Brain },
          { label: 'Safety rules applied', done: config.contentBoundaries.length > 0, icon: Shield },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={`preview-check-${item.label}`} className="flex items-center gap-2.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${item.done ? 'bg-primary/10' : 'bg-muted'}`}>
                {item.done ? <CheckCircle2 size={12} className="text-primary" /> : <Icon size={12} className="text-muted-foreground" />}
              </div>
              <p className={`text-xs ${item.done ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{item.label}</p>
            </div>
          );
        })}
      </div>

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground">Build progress</span>
          <span className="text-xs font-semibold text-foreground font-tabular">{completionPct}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full progress-bar-fill"
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>

      {/* Goals */}
      {config.goals.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Goals</p>
          <div className="flex flex-wrap gap-1.5">
            {config.goals.map((g) => (
              <span key={`preview-goal-${g}`} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{g}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}